import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

/**
 * Contact & Community — Update #172
 *
 * Public marketing/support page. Dark broadcast aesthetic with the
 * radio-vocabulary section structure specified in the build prompt.
 *
 * Non-Tailwind styling on purpose — this is a public page with its
 * own font stack (Space Grotesk + Inter) and self-contained tokens.
 * Keeps the visual language of a customer-facing site distinct from
 * the authenticated dashboard.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Design tokens (mirror the mockup exactly) ───────────────────────
const COLORS = {
  bgBase:     "#0f1117",
  bgSurface:  "#151823",
  bgCard:     "#1a1e2a",
  border:     "#262b3a",
  divider:    "#1f2330",
  accent:     "#4a9eff",
  accentTint: "#4a9eff1a",
  accentLine: "#4a9eff40",
  signal:     "#3ddc97",
  signalTint: "#3ddc971a",
  text:       "#e6e8ee",
  textDim:    "#8b93a7",
  textFaint:  "#565d72",
};

const TOPICS = [
  { id: "account_billing",     label: "Account & billing" },
  { id: "technical_support",   label: "Technical support" },
  { id: "feature_request",     label: "Feature request" },
  { id: "partnership_inquiry", label: "Partnership inquiry" },
  { id: "founding_feedback",   label: "Founding Tester feedback" },
  { id: "other",               label: "Something else" },
];

const COMMUNITY_CARDS = [
  {
    freq:  "101.FM",
    title: "Community forum",
    desc:  "Ask questions, share workflows, and get answers from stations running BroadcastFlow AI in production.",
    cta:   "Join the forum →",
    href:  "#",
  },
  {
    freq:  "102.FM",
    title: "Feature requests",
    desc:  "Vote on what gets built next, or pitch something we haven't thought of yet.",
    cta:   "View the roadmap →",
    href:  "#",
  },
  {
    freq:  "103.FM",
    title: "Frequency Log",
    desc:  "Every update, module launch, and fix — logged as it ships, no marketing spin.",
    cta:   "Read the changelog →",
    href:  "#",
  },
];

const FAQ_ITEMS = [
  {
    q: "How does billing work if I bring my own API keys?",
    a: "When you connect your own key (BYOK) for a provider, generations on that provider are billed directly by them — nothing runs against your BroadcastFlow AI quota. You keep the built-in Pro quota untouched for anything you haven't BYOK'd.",
  },
  {
    q: "Which modules ship with a shared platform key vs. require my own?",
    a: "Text (Claude), images (Nano Banana / GPT Image), Sora 2 video, and Whisper STT all run on our shared Emergent key with monthly caps. ElevenLabs voice, LALAL.AI stems, Mubert music beds, Kits AI vocalists, Synthesia/HeyGen avatars, and Stripe billing need your own key.",
  },
  {
    q: "How do I cancel my subscription?",
    a: "Go to Dashboard → Subscription and click 'Cancel plan'. Your Pro features stay active until the end of the current billing period — no pro-rated refunds, but no early cutoff either. You keep full library access on the free tier after cancellation.",
  },
  {
    q: "Do I own the content generated on the platform?",
    a: "Yes. Every script, audio file, image, and video generated through BroadcastFlow AI belongs to your station and can be exported at any time. We don't relicense customer output for training or resale.",
  },
  {
    q: "Can I export my data if I leave?",
    a: "Yes. Every module has a bulk-export option (audio + metadata as ZIP; scripts + analytics as CSV/JSON). If you cancel, your data stays available for export for 90 days before it's purged.",
  },
  {
    q: "Is there a free trial?",
    a: "Yes — 14 days of full Pro access, 50 AI generations included, one station, no credit card required. You can start from any 'Start free trial' button on the site.",
  },
];

// Prefers-reduced-motion — the animations respect the user's OS setting
const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const m = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(m.matches);
    const on = () => setReduced(m.matches);
    m.addEventListener?.("change", on);
    return () => m.removeEventListener?.("change", on);
  }, []);
  return reduced;
};

// ── Nav ─────────────────────────────────────────────────────────────
const Nav = ({ reducedMotion }) => (
  <nav
    data-testid="contact-nav"
    style={{
      position: "sticky", top: 0, zIndex: 40,
      background: "rgba(15,17,23,0.85)", backdropFilter: "blur(14px)",
      borderBottom: `1px solid ${COLORS.divider}`,
      fontFamily: "Inter, system-ui, sans-serif",
    }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "16px 24px",
                  display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <Link
        to="/"
        data-testid="contact-nav-logo"
        style={{ display: "flex", alignItems: "center", gap: 10, textDecoration: "none",
                 color: COLORS.text, fontFamily: "'Space Grotesk', Inter, sans-serif",
                 fontWeight: 600, fontSize: 16, letterSpacing: "0.02em" }}
      >
        <span
          style={{
            width: 10, height: 10, borderRadius: 99, background: COLORS.signal,
            boxShadow: `0 0 0 4px ${COLORS.signalTint}`,
            animation: reducedMotion ? "none" : "signal-pulse 2.2s ease-in-out infinite",
          }}
        />
        BroadcastFlow AI
      </Link>
      <div style={{ display: "flex", gap: 26, alignItems: "center",
                    fontSize: 13, letterSpacing: "0.01em" }}
           className="contact-nav-links">
        <a href="#contact"   style={navLinkStyle} data-testid="nav-link-contact">Contact</a>
        <a href="#community" style={navLinkStyle} data-testid="nav-link-community">Community</a>
        <a href="#spotlight" style={navLinkStyle} data-testid="nav-link-spotlight">Station Spotlight</a>
        <a href="/guide"     style={navLinkStyle} data-testid="nav-link-docs">Docs</a>
      </div>
    </div>
  </nav>
);

const navLinkStyle = {
  color: COLORS.textDim, textDecoration: "none", transition: "color 180ms ease",
};

// ── Hero + signal panel ─────────────────────────────────────────────
const Hero = ({ reducedMotion }) => {
  const bars = Array.from({ length: 12 });
  return (
    <section
      data-testid="contact-hero"
      style={{ padding: "80px 24px 60px", borderBottom: `1px solid ${COLORS.divider}` }}
    >
      <div style={{ maxWidth: 1200, margin: "0 auto",
                    display: "grid", gridTemplateColumns: "1.35fr 1fr", gap: 60,
                    alignItems: "center" }}
           className="hero-grid">
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
            <span style={{ display: "inline-block", width: 26, height: 2,
                           background: COLORS.signal, borderRadius: 2 }} />
            <span style={{
              fontFamily: "'Space Grotesk', Inter, sans-serif",
              fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
              color: COLORS.signal,
            }}>
              Contact & Community
            </span>
          </div>
          <h1 style={{
            margin: "0 0 20px",
            fontFamily: "'Space Grotesk', Inter, sans-serif",
            fontWeight: 600, fontSize: "clamp(36px, 5.5vw, 56px)",
            lineHeight: 1.06, color: COLORS.text, letterSpacing: "-0.02em",
          }} data-testid="hero-headline">
            We read every signal that comes in.
          </h1>
          <p style={{
            margin: "0 0 32px", fontSize: 16, lineHeight: 1.65,
            color: COLORS.textDim, maxWidth: 540,
          }}>
            Questions about your station&apos;s setup, a feature you need, or just want
            to talk shop with other broadcasters — this is the frequency for it.
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <a href="#contact"   data-testid="hero-cta-primary" style={btnPrimary}>Send a message</a>
            <a href="#community" data-testid="hero-cta-ghost"   style={btnGhost}>Join the community</a>
          </div>
        </div>
        <div
          data-testid="signal-panel"
          style={{
            background: COLORS.bgCard,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 16, padding: 28,
          }}
        >
          <div style={{
            display: "flex", justifyContent: "space-between", alignItems: "center",
            marginBottom: 22, fontSize: 12,
            fontFamily: "'Space Grotesk', Inter, sans-serif",
            letterSpacing: "0.16em", textTransform: "uppercase", color: COLORS.textDim,
          }}>
            <span>Support Channel</span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 8, color: COLORS.signal }}>
              <span style={{
                width: 8, height: 8, borderRadius: 99, background: COLORS.signal,
                animation: reducedMotion ? "none" : "signal-pulse 1.6s ease-in-out infinite",
              }} />
              On air
            </span>
          </div>
          <div style={{
            display: "flex", alignItems: "flex-end", gap: 6, height: 96,
            marginBottom: 22,
          }} aria-hidden="true">
            {bars.map((_, i) => (
              <span
                key={i}
                style={{
                  flex: 1,
                  height: `${28 + Math.sin(i * 0.9) * 32 + 20}%`,
                  background: `linear-gradient(180deg, ${COLORS.accent} 0%, ${COLORS.signal} 100%)`,
                  borderRadius: 2,
                  animation: reducedMotion ? "none" : `wave-bar 1.4s ${i * 0.08}s ease-in-out infinite`,
                  transformOrigin: "bottom",
                }}
              />
            ))}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline",
                        paddingTop: 18, borderTop: `1px solid ${COLORS.divider}` }}>
            <span style={{ color: COLORS.textDim, fontSize: 12,
                           fontFamily: "'Space Grotesk', Inter, sans-serif",
                           letterSpacing: "0.16em", textTransform: "uppercase" }}>
              Avg. response time
            </span>
            <span style={{ color: COLORS.text, fontFamily: "'Space Grotesk', Inter, sans-serif",
                           fontWeight: 600, fontSize: 16 }}>
              Under 4 hours
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

const btnPrimary = {
  display: "inline-flex", alignItems: "center", padding: "13px 22px",
  background: COLORS.accent, color: "#0f1117",
  fontFamily: "'Space Grotesk', Inter, sans-serif",
  fontWeight: 600, fontSize: 14, borderRadius: 9,
  textDecoration: "none", letterSpacing: "0.01em",
  transition: "transform 180ms ease, box-shadow 180ms ease",
};
const btnGhost = {
  display: "inline-flex", alignItems: "center", padding: "13px 22px",
  border: `1px solid ${COLORS.border}`, background: "transparent",
  color: COLORS.text, fontFamily: "'Space Grotesk', Inter, sans-serif",
  fontWeight: 500, fontSize: 14, borderRadius: 9,
  textDecoration: "none", letterSpacing: "0.01em",
  transition: "border-color 180ms ease, color 180ms ease",
};

// ── Section header ──────────────────────────────────────────────────
const SectionEyebrow = ({ children, testId }) => (
  <div data-testid={testId} style={{
    display: "inline-flex", alignItems: "center", gap: 10,
    fontFamily: "'Space Grotesk', Inter, sans-serif",
    fontSize: 11, letterSpacing: "0.28em", textTransform: "uppercase",
    color: COLORS.accent, marginBottom: 16,
  }}>
    <span style={{ display: "inline-block", width: 22, height: 2,
                   background: COLORS.accent, borderRadius: 2 }} />
    {children}
  </div>
);

// ── Contact section ─────────────────────────────────────────────────
const ContactSection = () => {
  const [form, setForm] = useState({
    name: "", station_name: "", email: "", topic: "account_billing", message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);   // { ticket_id } | null

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/support/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        const detail = body?.detail || body?.message || `Error ${r.status}`;
        toast.error(typeof detail === "string" ? detail : "Something went wrong. Please try again.");
        return;
      }
      setSubmitted({ ticket_id: body.ticket_id, email: form.email });
      toast.success("Message received — check your inbox for confirmation.");
      setForm({ name: "", station_name: "", email: "", topic: "account_billing", message: "" });
    } catch (err) {
      toast.error("Network error. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section"
             style={{ padding: "80px 24px", borderBottom: `1px solid ${COLORS.divider}` }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <SectionEyebrow testId="contact-eyebrow">Sign-On</SectionEyebrow>
        <h2 style={sectionH2}>Get in touch</h2>
        <p style={sectionLede}>
          Tell us what&apos;s going on with your station and we&apos;ll route it to the right person.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 40, marginTop: 32 }}
             className="contact-grid">
          {/* Form column */}
          <div style={{
            background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
            borderRadius: 16, padding: 32,
          }}>
            {submitted ? (
              <div data-testid="contact-form-success" style={{ padding: "8px 0" }}>
                <div style={{
                  display: "inline-flex", alignItems: "center", gap: 10,
                  padding: "6px 12px", borderRadius: 99,
                  background: COLORS.signalTint, color: COLORS.signal,
                  fontFamily: "'Space Grotesk', Inter, sans-serif",
                  fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase",
                  marginBottom: 20,
                }}>
                  <span style={{ width: 6, height: 6, borderRadius: 99, background: COLORS.signal }} />
                  Message received
                </div>
                <h3 style={{
                  margin: "0 0 12px",
                  fontFamily: "'Space Grotesk', Inter, sans-serif",
                  fontWeight: 600, fontSize: 22, color: COLORS.text,
                }}>
                  We&apos;ve picked up your signal.
                </h3>
                <p style={{ margin: 0, color: COLORS.textDim, fontSize: 14, lineHeight: 1.65 }}>
                  A confirmation has been sent to <strong style={{ color: COLORS.text }}>{submitted.email}</strong>.
                  Your reference number is <span style={{ fontFamily: "'JetBrains Mono', monospace",
                                                          color: COLORS.accent, fontSize: 13 }}
                                                  data-testid="contact-ticket-id">{submitted.ticket_id}</span>.
                  Someone from the team will follow up within one business day.
                </p>
                <button
                  type="button"
                  onClick={() => setSubmitted(null)}
                  data-testid="contact-send-another"
                  style={{ ...btnGhost, marginTop: 24, cursor: "pointer" }}
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                  <Field label="Your name" htmlFor="c-name">
                    <input id="c-name" data-testid="contact-input-name"
                           value={form.name} onChange={onChange("name")}
                           required maxLength={120} style={inputStyle} />
                  </Field>
                  <Field label="Station name" htmlFor="c-station">
                    <input id="c-station" data-testid="contact-input-station"
                           value={form.station_name} onChange={onChange("station_name")}
                           required maxLength={160} style={inputStyle} />
                  </Field>
                </div>
                <Field label="Email" htmlFor="c-email">
                  <input id="c-email" data-testid="contact-input-email"
                         type="email" value={form.email} onChange={onChange("email")}
                         required maxLength={254} style={inputStyle} />
                </Field>
                <Field label="What's this about" htmlFor="c-topic">
                  <select id="c-topic" data-testid="contact-input-topic"
                          value={form.topic} onChange={onChange("topic")}
                          style={{ ...inputStyle, appearance: "none",
                                   backgroundImage:
                                     "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'><path fill='%238b93a7' d='M2 4l4 4 4-4z'/></svg>\")",
                                   backgroundRepeat: "no-repeat",
                                   backgroundPosition: "right 14px center",
                                   paddingRight: 38 }}>
                    {TOPICS.map((t) => (
                      <option key={t.id} value={t.id}>{t.label}</option>
                    ))}
                  </select>
                </Field>
                <Field label="Message" htmlFor="c-msg">
                  <textarea id="c-msg" data-testid="contact-input-message"
                            value={form.message} onChange={onChange("message")}
                            required minLength={8} maxLength={4000} rows={6}
                            style={{ ...inputStyle, resize: "vertical", minHeight: 140,
                                     fontFamily: "Inter, sans-serif", lineHeight: 1.6 }} />
                </Field>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 8,
                              flexWrap: "wrap" }}>
                  <button type="submit" data-testid="contact-submit"
                          disabled={submitting}
                          style={{ ...btnPrimary, cursor: submitting ? "not-allowed" : "pointer",
                                   opacity: submitting ? 0.6 : 1, border: "none" }}>
                    {submitting ? "Sending…" : "Send message"}
                  </button>
                  <span style={{ color: COLORS.textFaint, fontSize: 12 }}>
                    We typically reply within one business day.
                  </span>
                </div>
              </form>
            )}
          </div>

          {/* Right column — support channels + status */}
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <ChannelCard icon="✉" title="Email support" desc="support@broadcastflow.ai" testId="channel-email" />
            <ChannelCard icon="◐" title="Live chat" desc="In-app, weekdays 9am–6pm ET" testId="channel-live-chat" />
            <ChannelCard icon="▤" title="Help center" desc="Guides for every module, updated weekly" testId="channel-help" />
            <div data-testid="status-strip" style={{
              marginTop: 4, padding: "14px 18px", borderRadius: 12,
              background: COLORS.signalTint, border: `1px solid ${COLORS.signal}55`,
              display: "flex", alignItems: "center", gap: 12,
              fontFamily: "'Space Grotesk', Inter, sans-serif",
              fontSize: 13, color: COLORS.signal, letterSpacing: "0.02em",
            }}>
              <span style={{ width: 8, height: 8, borderRadius: 99, background: COLORS.signal }} />
              All systems operational
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const sectionH2 = {
  margin: 0, fontFamily: "'Space Grotesk', Inter, sans-serif",
  fontWeight: 600, fontSize: "clamp(28px, 3.4vw, 40px)", color: COLORS.text,
  letterSpacing: "-0.015em", lineHeight: 1.1,
};
const sectionLede = {
  margin: "12px 0 0", color: COLORS.textDim, fontSize: 15,
  lineHeight: 1.65, maxWidth: 620,
};
const inputStyle = {
  width: "100%", padding: "12px 14px", background: COLORS.bgBase,
  border: `1px solid ${COLORS.border}`, borderRadius: 9,
  color: COLORS.text, fontFamily: "Inter, sans-serif", fontSize: 14,
  outline: "none", transition: "border-color 160ms ease, box-shadow 160ms ease",
};

const Field = ({ label, htmlFor, children }) => (
  <div style={{ marginTop: 16 }}>
    <label htmlFor={htmlFor} style={{
      display: "block", marginBottom: 8,
      fontFamily: "'Space Grotesk', Inter, sans-serif",
      fontSize: 11, letterSpacing: "0.16em", textTransform: "uppercase",
      color: COLORS.textDim,
    }}>{label}</label>
    {children}
  </div>
);

const ChannelCard = ({ icon, title, desc, testId }) => (
  <div data-testid={testId} style={{
    display: "flex", alignItems: "center", gap: 14,
    padding: "16px 18px", background: COLORS.bgCard,
    border: `1px solid ${COLORS.border}`, borderRadius: 12,
  }}>
    <span style={{
      width: 38, height: 38, borderRadius: 10,
      background: COLORS.accentTint, color: COLORS.accent,
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      fontSize: 18, fontWeight: 600, flexShrink: 0,
    }}>{icon}</span>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontFamily: "'Space Grotesk', Inter, sans-serif",
                    fontWeight: 600, fontSize: 14, color: COLORS.text }}>
        {title}
      </div>
      <div style={{ fontSize: 13, color: COLORS.textDim, marginTop: 2 }}>
        {desc}
      </div>
    </div>
  </div>
);

// ── Community section ───────────────────────────────────────────────
const CommunitySection = () => (
  <section id="community" data-testid="community-section"
           style={{ padding: "80px 24px", borderBottom: `1px solid ${COLORS.divider}`,
                    background: COLORS.bgSurface }}>
    <div style={{ maxWidth: 1200, margin: "0 auto" }}>
      <SectionEyebrow testId="community-eyebrow">On Air</SectionEyebrow>
      <h2 style={sectionH2}>Where broadcasters talk to broadcasters</h2>
      <p style={sectionLede}>
        Trade presets, troubleshoot builds, and see what other stations are shipping.
      </p>
      <div style={{
        display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, marginTop: 40,
      }} className="community-grid">
        {COMMUNITY_CARDS.map((c) => (
          <a key={c.freq} href={c.href}
             data-testid={`community-card-${c.freq.toLowerCase().replace(/\./g, "-")}`}
             style={{
               display: "block", padding: 28,
               background: COLORS.bgCard, border: `1px solid ${COLORS.border}`,
               borderRadius: 14, textDecoration: "none", color: "inherit",
               transition: "transform 220ms ease, border-color 220ms ease",
             }}
             onMouseEnter={(e) => { e.currentTarget.style.borderColor = COLORS.accentLine;
                                     e.currentTarget.style.transform = "translateY(-2px)"; }}
             onMouseLeave={(e) => { e.currentTarget.style.borderColor = COLORS.border;
                                     e.currentTarget.style.transform = "translateY(0)"; }}>
            <div style={{
              display: "inline-block", padding: "4px 10px", borderRadius: 99,
              background: COLORS.accentTint, color: COLORS.accent,
              fontFamily: "'JetBrains Mono', Menlo, monospace",
              fontSize: 11, letterSpacing: "0.12em", marginBottom: 20,
            }}>{c.freq}</div>
            <h3 style={{ margin: "0 0 10px", color: COLORS.text,
                         fontFamily: "'Space Grotesk', Inter, sans-serif",
                         fontWeight: 600, fontSize: 20 }}>{c.title}</h3>
            <p style={{ margin: "0 0 24px", color: COLORS.textDim, fontSize: 14, lineHeight: 1.6 }}>
              {c.desc}
            </p>
            <span style={{ color: COLORS.accent, fontSize: 13, fontWeight: 500 }}>
              {c.cta}
            </span>
          </a>
        ))}
      </div>
    </div>
  </section>
);

// ── FAQ / Cue Sheet ─────────────────────────────────────────────────
const FaqSection = () => {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <section id="faq" data-testid="faq-section"
             style={{ padding: "80px 24px", borderBottom: `1px solid ${COLORS.divider}` }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <SectionEyebrow testId="faq-eyebrow">Cue Sheet</SectionEyebrow>
        <h2 style={sectionH2}>Frequently Asked</h2>
        <p style={sectionLede}>
          The questions we get most often — expanded so you don&apos;t have to file a ticket for them.
        </p>
        <div style={{ marginTop: 40, display: "flex", flexDirection: "column", gap: 8 }}
             data-testid="faq-list">
          {FAQ_ITEMS.map((item, i) => {
            const open = openIdx === i;
            return (
              <div key={i} data-testid={`faq-item-${i}`}
                   style={{ background: COLORS.bgCard,
                            border: `1px solid ${open ? COLORS.accentLine : COLORS.border}`,
                            borderRadius: 12, overflow: "hidden",
                            transition: "border-color 220ms ease" }}>
                <button
                  type="button"
                  onClick={() => setOpenIdx(open ? -1 : i)}
                  aria-expanded={open}
                  data-testid={`faq-toggle-${i}`}
                  style={{
                    width: "100%", background: "transparent", border: "none", cursor: "pointer",
                    padding: "18px 22px", display: "flex", alignItems: "center",
                    justifyContent: "space-between", gap: 16, textAlign: "left",
                    color: COLORS.text, fontFamily: "'Space Grotesk', Inter, sans-serif",
                    fontWeight: 500, fontSize: 15, letterSpacing: "-0.005em",
                  }}
                >
                  <span>{item.q}</span>
                  <span style={{
                    color: open ? COLORS.accent : COLORS.textDim, fontSize: 20,
                    transition: "transform 220ms ease, color 220ms ease",
                    transform: open ? "rotate(45deg)" : "rotate(0deg)",
                    lineHeight: 1, display: "inline-block", flexShrink: 0,
                  }}>+</span>
                </button>
                <div style={{
                  maxHeight: open ? 400 : 0, overflow: "hidden",
                  transition: "max-height 260ms ease",
                }}>
                  <div style={{ padding: "0 22px 22px", color: COLORS.textDim,
                                fontSize: 14, lineHeight: 1.7 }}>
                    {item.a}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ── Station Spotlight (rotating testimonial component) ──────────────
const TESTIMONIALS = [
  {
    quote: "We went from manually clipping our morning show for social to publishing it in minutes with Vision Studio. Our engagement didn't just grow — it stayed consistent.",
    name:  "Program Director",
    station: "independent FM station",
    avatarGradient: "linear-gradient(135deg, #4a9eff, #3ddc97)",
  },
  {
    quote: "Being able to spin up a 30-second spot at 3am when a sponsor drops in a rush order — that's the kind of thing that used to derail my whole week.",
    name:  "Owner / GM",
    station: "community AM/FM station",
    avatarGradient: "linear-gradient(135deg, #f59e0b, #4a9eff)",
  },
  {
    quote: "The Local News module changed how our morning drive sounds. Fresh headlines, paraphrased with real editorial voice, ready to read.",
    name:  "News Director",
    station: "regional broadcast group",
    avatarGradient: "linear-gradient(135deg, #a855f7, #3ddc97)",
  },
];

const StationSpotlight = () => {
  const [idx, setIdx] = useState(0);
  const t = TESTIMONIALS[idx];
  return (
    <section id="spotlight" data-testid="spotlight-section"
             style={{ padding: "80px 24px", borderBottom: `1px solid ${COLORS.divider}`,
                      background: COLORS.bgSurface }}>
      <div style={{ maxWidth: 900, margin: "0 auto" }}>
        <SectionEyebrow testId="spotlight-eyebrow">Station Spotlight</SectionEyebrow>
        <h2 style={sectionH2}>Built by broadcasters, for broadcasters</h2>
        <div style={{
          marginTop: 40, padding: "40px 40px 36px", background: COLORS.bgCard,
          border: `1px solid ${COLORS.border}`, borderRadius: 16,
        }} data-testid="spotlight-card">
          <div style={{ fontSize: 40, color: COLORS.accent, lineHeight: 1, marginBottom: 8,
                        fontFamily: "'Space Grotesk', Inter, sans-serif" }}>“</div>
          <blockquote style={{
            margin: 0, fontSize: "clamp(18px, 2.2vw, 22px)", lineHeight: 1.5, color: COLORS.text,
            fontFamily: "'Space Grotesk', Inter, sans-serif", fontWeight: 500,
            letterSpacing: "-0.005em",
          }}>{t.quote}</blockquote>
          <div style={{ display: "flex", alignItems: "center", gap: 14, marginTop: 28 }}>
            <div style={{
              width: 46, height: 46, borderRadius: 99, background: t.avatarGradient,
              flexShrink: 0,
            }} />
            <div>
              <div style={{ fontFamily: "'Space Grotesk', Inter, sans-serif",
                            fontWeight: 600, fontSize: 15, color: COLORS.text }}>
                {t.name}
              </div>
              <div style={{ fontSize: 13, color: COLORS.textDim }}>
                {t.station}
              </div>
            </div>
          </div>
        </div>
        {TESTIMONIALS.length > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 20 }}
               data-testid="spotlight-dots">
            {TESTIMONIALS.map((_, i) => (
              <button key={i}
                      onClick={() => setIdx(i)}
                      aria-label={`Show testimonial ${i + 1}`}
                      data-testid={`spotlight-dot-${i}`}
                      style={{
                        width: idx === i ? 22 : 8, height: 8, borderRadius: 99,
                        background: idx === i ? COLORS.accent : COLORS.border,
                        border: "none", cursor: "pointer",
                        transition: "width 220ms ease, background 220ms ease",
                      }} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

// ── Footer (with social links) ──────────────────────────────────────
const Footer = () => (
  <footer data-testid="contact-footer" style={{
    padding: "40px 24px 36px", background: COLORS.bgBase, borderTop: `1px solid ${COLORS.divider}`,
  }}>
    <div style={{
      maxWidth: 1200, margin: "0 auto",
      display: "flex", justifyContent: "space-between", alignItems: "center",
      flexWrap: "wrap", gap: 20,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, color: COLORS.text,
                    fontFamily: "'Space Grotesk', Inter, sans-serif",
                    fontWeight: 600, fontSize: 15 }}>
        <span style={{ width: 8, height: 8, borderRadius: 99, background: COLORS.signal }} />
        BroadcastFlow AI
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center",
                    gap: 24, fontSize: 13, color: COLORS.textFaint }}>
        <Link to="/privacy" style={footerLinkStyle} data-testid="footer-privacy">Privacy</Link>
        <Link to="/terms"   style={footerLinkStyle} data-testid="footer-terms">Terms</Link>
        <a href="#faq"      style={footerLinkStyle} data-testid="footer-status">Status</a>
        <a href="#contact"  style={footerLinkStyle} data-testid="footer-contact">Contact</a>
      </div>
      <div style={{ display: "flex", gap: 14, alignItems: "center" }} data-testid="footer-socials">
        <SocialIcon href="https://twitter.com/broadcastflowai" label="X" testId="social-x"
                    svgPath="M18.244 2H21l-6.51 7.44L22 22h-6.19l-4.85-6.34L5.4 22H2.643l6.98-7.98L2 2h6.35l4.37 5.77L18.244 2Zm-2.17 18h1.71L7.98 4H6.17l9.905 16Z" />
        <SocialIcon href="https://instagram.com/broadcastflowai" label="Instagram" testId="social-ig"
                    svgPath="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.42.56.22.96.48 1.38.9.42.42.68.82.9 1.38.17.42.37 1.06.42 2.23.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.42 2.23-.22.56-.48.96-.9 1.38-.42.42-.82.68-1.38.9-.42.17-1.06.37-2.23.42-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.23-.42-.56-.22-.96-.48-1.38-.9a3.7 3.7 0 0 1-.9-1.38c-.17-.42-.37-1.06-.42-2.23C2.21 15.58 2.2 15.2 2.2 12s.01-3.58.07-4.85c.05-1.17.25-1.8.42-2.23.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.17 1.06-.37 2.23-.42C8.42 2.21 8.8 2.2 12 2.2Zm0 1.98c-3.15 0-3.5.01-4.75.07-.99.05-1.53.21-1.88.35-.47.18-.81.4-1.16.75-.35.35-.57.69-.75 1.16-.14.35-.3.89-.35 1.88-.06 1.25-.07 1.6-.07 4.75s.01 3.5.07 4.75c.05.99.21 1.53.35 1.88.18.47.4.81.75 1.16.35.35.69.57 1.16.75.35.14.89.3 1.88.35 1.25.06 1.6.07 4.75.07s3.5-.01 4.75-.07c.99-.05 1.53-.21 1.88-.35.47-.18.81-.4 1.16-.75.35-.35.57-.69.75-1.16.14-.35.3-.89.35-1.88.06-1.25.07-1.6.07-4.75s-.01-3.5-.07-4.75c-.05-.99-.21-1.53-.35-1.88a3.13 3.13 0 0 0-.75-1.16 3.13 3.13 0 0 0-1.16-.75c-.35-.14-.89-.3-1.88-.35-1.25-.06-1.6-.07-4.75-.07Zm0 3.37a4.45 4.45 0 1 1 0 8.9 4.45 4.45 0 0 1 0-8.9Zm0 1.98a2.47 2.47 0 1 0 0 4.94 2.47 2.47 0 0 0 0-4.94Zm4.66-2.13a1.04 1.04 0 1 1 0 2.08 1.04 1.04 0 0 1 0-2.08Z" />
        <SocialIcon href="https://youtube.com/@broadcastflowai" label="YouTube" testId="social-yt"
                    svgPath="M23.5 6.5a3 3 0 0 0-2.1-2.1C19.5 4 12 4 12 4s-7.5 0-9.4.4a3 3 0 0 0-2.1 2.1C.1 8.4.1 12 .1 12s0 3.6.4 5.5a3 3 0 0 0 2.1 2.1C4.5 20 12 20 12 20s7.5 0 9.4-.4a3 3 0 0 0 2.1-2.1c.4-1.9.4-5.5.4-5.5s0-3.6-.4-5.5ZM9.75 15.5v-7l6.25 3.5-6.25 3.5Z" />
        <SocialIcon href="https://linkedin.com/company/broadcastflowai" label="LinkedIn" testId="social-in"
                    svgPath="M20.45 20.45h-3.55v-5.57c0-1.33-.02-3.04-1.85-3.04-1.86 0-2.14 1.45-2.14 2.94v5.67H9.36V9h3.41v1.56h.05a3.73 3.73 0 0 1 3.36-1.85c3.6 0 4.27 2.37 4.27 5.45v6.29ZM5.34 7.44a2.06 2.06 0 1 1 0-4.12 2.06 2.06 0 0 1 0 4.12Zm1.78 13.01H3.56V9h3.56v11.45ZM22.23 0H1.77C.79 0 0 .78 0 1.74v20.52C0 23.22.79 24 1.77 24h20.46c.98 0 1.77-.78 1.77-1.74V1.74C24 .78 23.21 0 22.23 0Z" />
      </div>
    </div>
    <div style={{ maxWidth: 1200, margin: "24px auto 0", fontSize: 12,
                  color: COLORS.textFaint, textAlign: "center" }}>
      © 2026 BroadcastFlow AI · Built for broadcasters
    </div>
  </footer>
);

const footerLinkStyle = {
  color: COLORS.textFaint, textDecoration: "none",
  transition: "color 160ms ease",
};

const SocialIcon = ({ href, label, svgPath, testId }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" aria-label={label}
     data-testid={testId}
     style={{ color: COLORS.textFaint, display: "inline-flex",
              transition: "color 180ms ease" }}
     onMouseEnter={(e) => { e.currentTarget.style.color = COLORS.text; }}
     onMouseLeave={(e) => { e.currentTarget.style.color = COLORS.textFaint; }}>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d={svgPath} />
    </svg>
  </a>
);

// ── Live chat widget (stubbed launcher + modal) ─────────────────────
const LiveChatWidget = () => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        data-testid="live-chat-launcher"
        onClick={() => setOpen(true)}
        aria-label="Open live chat"
        style={{
          position: "fixed", right: 24, bottom: 24, zIndex: 60,
          width: 56, height: 56, borderRadius: 99, border: "none",
          background: COLORS.accent, color: COLORS.bgBase,
          boxShadow: `0 12px 32px ${COLORS.accent}55, 0 0 0 6px ${COLORS.accentTint}`,
          cursor: "pointer", display: "inline-flex",
          alignItems: "center", justifyContent: "center",
          transition: "transform 200ms ease",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
        onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
      >
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-5 4V6a2 2 0 0 1 2-2Z"
                stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          data-testid="live-chat-modal"
          onClick={() => setOpen(false)}
          role="dialog" aria-modal="true"
          style={{
            position: "fixed", inset: 0, zIndex: 65,
            background: "rgba(0,0,0,0.55)", backdropFilter: "blur(6px)",
            display: "flex", alignItems: "flex-end", justifyContent: "flex-end",
            padding: 24,
          }}>
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%", maxWidth: 380, background: COLORS.bgCard,
              border: `1px solid ${COLORS.border}`, borderRadius: 16,
              padding: "20px 22px 22px", color: COLORS.text,
              fontFamily: "Inter, sans-serif",
            }}>
            <div style={{ display: "flex", justifyContent: "space-between",
                          alignItems: "center", marginBottom: 6 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ width: 8, height: 8, borderRadius: 99, background: COLORS.signal }} />
                <span style={{ fontFamily: "'Space Grotesk', Inter, sans-serif",
                               fontWeight: 600, fontSize: 15 }}>Live chat</span>
              </div>
              <button type="button" onClick={() => setOpen(false)}
                      data-testid="live-chat-close"
                      style={{ background: "transparent", border: "none",
                               color: COLORS.textDim, cursor: "pointer",
                               fontSize: 20, lineHeight: 1, padding: 4 }}>×</button>
            </div>
            <div style={{ marginTop: 12, padding: 14, background: COLORS.bgBase,
                          border: `1px solid ${COLORS.border}`, borderRadius: 10,
                          fontSize: 13, color: COLORS.textDim, lineHeight: 1.6 }}>
              Our live-chat channel is currently in early access. In the meantime,
              drop a message via the form above and we&apos;ll respond within one
              business day.
            </div>
            <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap" }}>
              <a href="#contact"
                 onClick={() => setOpen(false)}
                 data-testid="live-chat-open-form"
                 style={{ ...btnPrimary, padding: "10px 16px", fontSize: 13, flex: 1,
                          justifyContent: "center" }}>
                Open contact form
              </a>
              <a href="mailto:support@broadcastflow.ai"
                 data-testid="live-chat-email"
                 style={{ ...btnGhost, padding: "10px 16px", fontSize: 13, flex: 1,
                          justifyContent: "center" }}>
                Email us
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ── Page-level style injection (fonts + animations) ─────────────────
const StyleInject = () => (
  <style dangerouslySetInnerHTML={{ __html: `
    @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
    body { background: ${COLORS.bgBase}; }
    @keyframes signal-pulse {
      0%, 100% { transform: scale(1);   opacity: 1;   }
      50%      { transform: scale(1.4); opacity: 0.6; }
    }
    @keyframes wave-bar {
      0%, 100% { transform: scaleY(0.55); }
      50%      { transform: scaleY(1.15); }
    }
    input:focus, textarea:focus, select:focus {
      border-color: ${COLORS.accent} !important;
      box-shadow: 0 0 0 3px ${COLORS.accentTint};
    }
    a:focus-visible, button:focus-visible {
      outline: 2px solid ${COLORS.accent}; outline-offset: 3px;
    }
    ${''}
    /* Nav hover */
    .contact-nav-links a:hover { color: ${COLORS.text}; }
    @media (max-width: 640px) {
      .contact-nav-links { display: none !important; }
    }
    @media (max-width: 900px) {
      .hero-grid, .contact-grid { grid-template-columns: 1fr !important; }
      .community-grid { grid-template-columns: 1fr !important; }
    }
    @media (prefers-reduced-motion: reduce) {
      *, *::before, *::after { animation: none !important; transition: none !important; }
    }
  ` }} />
);

// ── Page shell ──────────────────────────────────────────────────────
export default function ContactCommunity() {
  const reducedMotion = usePrefersReducedMotion();
  const rootRef = useRef(null);

  useEffect(() => {
    document.title = "Contact & Community — BroadcastFlow AI";
  }, []);

  return (
    <div ref={rootRef} data-testid="contact-community-page"
         style={{ background: COLORS.bgBase, color: COLORS.text,
                  minHeight: "100vh", fontFamily: "Inter, system-ui, sans-serif" }}>
      <StyleInject />
      <Nav reducedMotion={reducedMotion} />
      <Hero reducedMotion={reducedMotion} />
      <ContactSection />
      <CommunitySection />
      <FaqSection />
      <StationSpotlight />
      <Footer />
      <LiveChatWidget />
      <Toaster theme="dark" position="top-center" />
    </div>
  );
}
